import SearchBar from '_/components/SearchBar';
import Text from '_/components/Text';
import Title from '_/components/Title';
import { colors, fontFamily, spacing } from '_/constants/theme';
import { usersSlotsApi } from '_/services/api';
import { UsersModel } from '_/services/models/users.model';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, FlatList, ActivityIndicator, Platform, ScrollView } from 'react-native';
import FlashMessage from 'react-native-flash-message';
import Modal from 'react-native-modal';

import Member from './Member';

interface SearchForMembersModalProps {
  visible: boolean;
  hideDropdown: () => void;
  slotId: string;
  setForMember: React.Dispatch<React.SetStateAction<UsersModel | undefined>>;
}

const PAGE_SIZE = 100;

export default function SearchForMembersModal({
  visible,
  hideDropdown,
  slotId,
  setForMember,
}: SearchForMembersModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [members, setMembers] = useState<UsersModel[]>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>();

  const { t } = useTranslation();
  const modalMessage = useRef<any>(null);

  const skipRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const fetchGenerationRef = useRef(0);

  const getUsersSlot = useCallback(
    async (slotId: string) => {
      const generation = ++fetchGenerationRef.current;
      skipRef.current = 0;
      hasMoreRef.current = true;
      loadingRef.current = true;
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setLoading(true);

      try {
        const result = await usersSlotsApi.getItem(slotId, {
          params: {
            ...(searchValue && {
              'name[$iLike]': `${searchValue}%`,
            }),
            '$sort[name]': 1,
            $limit: PAGE_SIZE,
            $skip: 0,
          },
        });

        if (generation !== fetchGenerationRef.current) {
          return;
        }

        setMembers(result.data);
        skipRef.current = result.data.length;
        hasMoreRef.current = result.data.length >= PAGE_SIZE;
      } catch {
        modalMessage.current.showMessage({
          message: 'Erro!',
          description: 'Houve um erro com a pesquisa. Por favor, tente novamente mais tarde',
          backgroundColor: colors.errorRed,
        });
      } finally {
        if (generation === fetchGenerationRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [searchValue]
  );

  const loadMoreUsers = useCallback(async () => {
    if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) {
      return;
    }

    const generation = fetchGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const result = await usersSlotsApi.getItem(slotId, {
        params: {
          ...(searchValue && {
            'name[$iLike]': `${searchValue}%`,
          }),
          '$sort[name]': 1,
          $limit: PAGE_SIZE,
          $skip: skipRef.current,
        },
      });

      if (generation !== fetchGenerationRef.current) {
        return;
      }

      if (result.data.length > 0) {
        setMembers((currentMembers) => [...currentMembers, ...result.data]);
        skipRef.current += result.data.length;
      }

      hasMoreRef.current = result.data.length >= PAGE_SIZE;
    } catch {
      hasMoreRef.current = false;
      modalMessage.current.showMessage({
        message: 'Erro!',
        description: 'Houve um erro com a pesquisa. Por favor, tente novamente mais tarde',
        backgroundColor: colors.errorRed,
      });
    } finally {
      if (generation === fetchGenerationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [searchValue, slotId]);

  useEffect(() => {
    if (visible) {
      getUsersSlot(slotId);
    }
  }, [getUsersSlot, slotId, visible]);

  const handleWebScroll = useCallback(
    (event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;

      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);

      if (distanceFromBottom <= 200) {
        loadMoreUsers();
      }
    },
    [loadMoreUsers]
  );

  return (
    <Modal
      style={styles.modal}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={visible}
      coverScreen
      hasBackdrop
      propagateSwipe
      backdropOpacity={0.5}
      onBackdropPress={() => hideDropdown()}
      onBackButtonPress={() => hideDropdown()}
    >
      <FlashMessage
        floating
        position="top"
        duration={5000}
        titleStyle={{ fontFamily: fontFamily.bold }}
        textStyle={{ fontFamily: fontFamily.regular }}
        ref={modalMessage}
      />

      <Title
        title={t('workstationScreen.searchMemberModal.searchMember')}
        hasCloseButton
        onPress={() => hideDropdown()}
        style={[styles.title, { maxWidth: '90%' }]}
      />

      <View style={styles.modalContent}>
        <SearchBar
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          placeholder={t('workstationScreen.searchMemberModal.search')}
        />

        {Platform.OS === 'web' ? (
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="always"
            style={styles.list}
            onScroll={handleWebScroll}
            scrollEventThrottle={100}
          >
            {loading && <ActivityIndicator color={colors.black} />}

            {!loading && members.length === 0 && (
              <Text>{t('workstationScreen.searchMemberModal.noResults')}</Text>
            )}

            {!loading &&
              members.map((member) => (
                <Member
                  key={member.id}
                  user={member}
                  setForMember={setForMember}
                  hideDropdown={hideDropdown}
                />
              ))}

            {loadingMore && <ActivityIndicator color={colors.black} />}
          </ScrollView>
        ) : (
          <FlatList
            data={members}
            keyExtractor={(item) => item.id}
            style={styles.list}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="always"
            onEndReached={loadMoreUsers}
            onEndReachedThreshold={0.5}
            ListEmptyComponent={
              loading ? (
                <ActivityIndicator color={colors.black} />
              ) : (
                <Text>{t('workstationScreen.searchMemberModal.noResults')}</Text>
              )
            }
            ListFooterComponent={loadingMore ? <ActivityIndicator color={colors.black} /> : null}
            renderItem={({ item }) => (
              <Member user={item} setForMember={setForMember} hideDropdown={hideDropdown} />
            )}
          />
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    width: '100%',
    ...(Platform.OS === 'web' && { maxWidth: 550 }),
    margin: 0,
    marginTop: 80,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    backgroundColor: colors.clear,
    borderTopRightRadius: 20,
    borderTopLeftRadius: 20,
    paddingVertical: 20,
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: spacing.sm,
  },
  list: {
    marginTop: 32,
    flex: 1,
  },
  title: {
    marginBottom: spacing.md,
  },
});
